import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Copy, ListOrdered, Loader2, Timer } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import RoundCountdown from '../../components/competition/RoundCountdown';
import DuplicateRoundDialog from '../components/DuplicateRoundDialog';
import UnclassedBlocksCard from '../components/UnclassedBlocksCard';
import { HeaderActions } from '../layout/header-actions';
import { competitionRoundsService } from '../../services/competition-rounds';
import { competitionsService } from '../../services/competitions';
import { datetimeLocalToIso, isoToDatetimeLocal } from '../../utils/datetime-local';
import { refreshCompetitionRounds } from '../hooks/useCompetitionRounds';
import { useCurrentLocation } from '../hooks/useCurrentLocation';
import { useGymAdmin } from '../context/gym-admin-context';
import { STATUS_VARIANT } from '../round-status';
import {
  LEAD_CLIMB_STYLES,
  leadStyleLabelKey,
  type LeadClassStyle,
  type LeadClimbStyle,
} from '../../components/competition/lead-climb-style';
import type { CompetitionCategory } from '../../types/competition';
import type { CompetitionRound } from '../../types/competition-round';

/** The round's block counts as the inputs hold them: one string per class. */
function blocksToDraft(round: CompetitionRound): Record<string, string> {
  return Object.fromEntries(
    Object.entries(round.block_counts ?? {}).map(([id, count]) => [id, String(count)])
  );
}

/** A block count as typed. An emptied box counts as none. */
function parseBlocks(value: string | undefined): number | null {
  const trimmed = (value ?? '').trim();
  if (trimmed === '') return 0;
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
}

/** The routes' holds as the inputs hold them: one string per route. */
function holdsToDraft(round: CompetitionRound): Record<string, string> {
  return Object.fromEntries(
    (round.round_climbs ?? []).map((climb) => [
      climb.id,
      climb.holds_count == null ? '' : String(climb.holds_count),
    ])
  );
}

/**
 * A holds count as typed. An emptied box is a route nobody has measured yet —
 * which is a round that will not start, not an invalid form — so it reads as
 * null rather than as an error.
 */
function parseHoldsCount(value: string | undefined): number | null | 'invalid' {
  const trimmed = (value ?? '').trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) && parsed > 0 && parsed <= 200 ? parsed : 'invalid';
}

/** Each route's forced style as the selects hold it: '' is no forcing. */
function forcedStylesToDraft(round: CompetitionRound): Record<string, string> {
  return Object.fromEntries(
    (round.round_climbs ?? []).map((climb) => [climb.id, climb.required_climb_style ?? ''])
  );
}

/** A turn's seat limit as the input holds it: empty is no limit. */
function seatsToDraft(round: CompetitionRound): string {
  return round.max_participants ? String(round.max_participants) : '';
}

/** A seat limit as typed. An emptied box takes the limit off. */
function parseSeats(value: string): number | null | 'invalid' {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 'invalid';
}

/** What the round is called and when it runs, as the form holds it. */
interface ScheduleDraft {
  name: string;
  startTime: string;
  duration: string;
}

function toSchedule(round: CompetitionRound): ScheduleDraft {
  return {
    name: round.name,
    startTime: isoToDatetimeLocal(round.start_time),
    duration: round.duration_minutes ? String(round.duration_minutes) : '',
  };
}

/**
 * One round, whole: what it is called, when it runs, where it is in its life,
 * and what it is made of.
 *
 * Everything about the round that is touched once and then left alone is here,
 * which is why there is no list page above it — a round is a row in the menu, and
 * the two things one does to the list of them (make one, unmake one) are on that
 * row (see `AppSidebar`).
 *
 * **Where it is in its life is said here and changed on the board.** Starting a
 * round, stopping its clock and closing it are the day's decisions, taken at the
 * desk with the scoreboard open — so the toggle that makes them lives there
 * (`round-scores.tsx`) and this page only names the state it is in.
 *
 * **The shape is settled before the clock starts.** Once the round leaves
 * `draft` the server refuses both edits — the blocks' numbers are what the sheet
 * at the wall says and what the judges are standing at (see
 * `CompetitionRoundCategory` and `CompetitionRound#set_block_count!`) — so the
 * inputs are shown disabled rather than offering a save that would come back an
 * error. Putting the round back to not-started, from the board, is the way back.
 *
 * The board has the round's own URL (`/rounds/:roundId`) and this page sits
 * behind it at `/edit`: that one is the screen of the day, while everything here
 * is touched once and then left alone.
 */
export default function RoundDetailPage() {
  const { roundId } = useParams<{ roundId: string }>();
  const navigate = useNavigate();
  const { placePath } = useGymAdmin();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { location: competition, loading: competitionLoading, slug } = useCurrentLocation();

  const [round, setRound] = useState<CompetitionRound | null>(null);
  const [categories, setCategories] = useState<CompetitionCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [duplicating, setDuplicating] = useState(false);

  /** The name, the planned start and the length, as typed. */
  const [schedule, setSchedule] = useState<ScheduleDraft>({
    name: '',
    startTime: '',
    duration: '',
  });

  /**
   * How many blocks each class is being given, as typed. Strings rather than
   * numbers so an input can be emptied while somebody retypes it — an empty box
   * is a half-typed number here, not an answer, and is read back as zero only
   * when it is saved.
   */
  const [blockDrafts, setBlockDrafts] = useState<Record<string, string>>({});

  /**
   * How many holds each route has, as typed — a lead round only. Strings for
   * the same reason the block counts are: an emptied box is somebody retyping,
   * and it is read back as "not measured" when it is saved.
   */
  const [holdsDrafts, setHoldsDrafts] = useState<Record<string, string>>({});

  /**
   * Which routes are forced to one style, as the selects hold it — a lead round
   * only, and only where the class climbing the route may do either. Saved with
   * the holds, one PATCH per changed route.
   */
  const [forcedDrafts, setForcedDrafts] = useState<Record<string, string>>({});

  /** How many may book the turn, as typed. Empty is no limit. */
  const [seatsDraft, setSeatsDraft] = useState('');

  const load = useCallback(
    async (competitionSlug: string, id: string) => {
      setLoading(true);
      try {
        const [loadedRound, loadedCategories] = await Promise.all([
          competitionRoundsService.getRound(competitionSlug, id),
          competitionsService.getCategories(competitionSlug),
        ]);
        setRound(loadedRound);
        setCategories(loadedCategories);
        setBlockDrafts(blocksToDraft(loadedRound));
        setHoldsDrafts(holdsToDraft(loadedRound));
        setForcedDrafts(forcedStylesToDraft(loadedRound));
        setSchedule(toSchedule(loadedRound));
        setSeatsDraft(seatsToDraft(loadedRound));
      } catch (error) {
        toast({
          title: t('gymAdmin.rounds.loadError'),
          description: error instanceof Error ? error.message : t('errors.unexpectedError'),
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    },
    [t, toast]
  );

  useEffect(() => {
    if (slug && roundId) load(slug, roundId);
  }, [slug, roundId, load]);

  const linked = useMemo(() => new Set(round?.category_ids ?? []), [round?.category_ids]);

  /**
   * Whether the round's shape can still be changed. The server's rule, drawn:
   * classes and block counts belong to a draft, and anything further on is
   * reset first.
   */
  const editable = round?.status === 'draft';

  /**
   * A lead round is made of routes, and a route carries one thing a block does
   * not: the holds on the wall. Two things follow — the counts above are "how
   * many routes", and there is a second form under them for the measurements.
   */
  const isLead = round?.round_type === 'Lead';

  /**
   * The round's routes, grouped by the class that climbs them, in the order the
   * sheet at the wall numbers them. A route the whole round shares — which is
   * what every round climb linked to one of the gym's routes is — is grouped
   * under no class.
   */
  const routeGroups = useMemo(() => {
    if (!isLead) return [];

    const climbs = [...(round?.round_climbs ?? [])].sort((a, b) => a.position - b.position);
    const groups: { category: CompetitionCategory | null; routes: typeof climbs }[] = [];

    const shared = climbs.filter((climb) => climb.competition_category_id === null);
    if (shared.length > 0) groups.push({ category: null, routes: shared });

    for (const category of categories) {
      if (!linked.has(category.id)) continue;
      const routes = climbs.filter((climb) => climb.competition_category_id === category.id);
      if (routes.length > 0) groups.push({ category, routes });
    }

    return groups;
  }, [isLead, round?.round_climbs, categories, linked]);

  /** The routes still waiting to be measured — what stops the round starting. */
  const unmeasured = useMemo(
    () => (round?.round_climbs ?? []).filter((climb) => climb.holds_count == null).length,
    [round?.round_climbs]
  );

  /**
   * Whether this round is a turn its classes' athletes book. Saved on the spot,
   * like the class ticks, and only on a draft — who is on a board is not
   * changed under the judges. Unmarking lets the bookings go, so the question
   * says how many there are first.
   */
  const toggleBookable = async () => {
    if (!slug || !round) return;
    const next = !round.bookable;
    const booked = round.bookings_count ?? 0;
    if (!next && booked > 0 && !window.confirm(t('gymAdmin.rounds.bookable.confirmOff', { count: booked }))) {
      return;
    }

    setBusy('bookable');
    try {
      const saved = await competitionRoundsService.updateRound(slug, round.id, { bookable: next });
      setRound((current) =>
        current ? { ...current, bookable: saved.bookable, bookings_count: saved.bookings_count } : current
      );
    } catch (error) {
      toast({
        title: t('gymAdmin.roundScores.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setBusy(null);
    }
  };

  /**
   * How many athletes may book this turn. Unlike whether it is one, this can
   * change after it has started: it only decides who may still book it, and
   * lowering it under the bookings already made unbooks nobody.
   */
  const saveSeats = async () => {
    if (!slug || !round) return;
    const seats = parseSeats(seatsDraft);
    if (seats === 'invalid') {
      toast({ title: t('gymAdmin.rounds.bookable.maxParticipantsInvalid'), variant: 'destructive' });
      return;
    }

    setBusy('seats');
    try {
      const saved = await competitionRoundsService.updateRound(slug, round.id, { max_participants: seats });
      setRound((current) =>
        current
          ? {
              ...current,
              max_participants: saved.max_participants,
              seats_left: saved.seats_left,
              seats_taken: saved.seats_taken,
              bookings_count: saved.bookings_count,
            }
          : current
      );
      setSeatsDraft(seatsToDraft(saved));
    } catch (error) {
      toast({
        title: t('gymAdmin.roundScores.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setBusy(null);
    }
  };

  /**
   * The limit — as typed, or as saved while the box holds nothing valid —
   * when it is under the seats already taken: allowed, since the organiser
   * may overbook, but said, because from the app nobody else can enter it.
   */
  const overbooked = useMemo(() => {
    if (!round?.bookable) return null;
    const typed = parseSeats(seatsDraft);
    const limit = typed === 'invalid' ? round.max_participants ?? null : typed;
    return limit !== null && (round.seats_taken ?? 0) > limit ? limit : null;
  }, [round?.bookable, round?.max_participants, round?.seats_taken, seatsDraft]);

  const toggleCategory = async (categoryId: string) => {
    if (!slug || !round) return;
    const next = new Set(linked);
    if (next.has(categoryId)) next.delete(categoryId);
    else next.add(categoryId);

    setBusy('categories');
    try {
      const saved = await competitionRoundsService.updateRound(slug, round.id, {
        category_ids: Array.from(next),
      });
      setRound(saved);
      if (slug && roundId) await load(slug, roundId);
    } catch (error) {
      toast({
        title: t('gymAdmin.roundScores.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setBusy(null);
    }
  };

  /**
   * How a class climbs this round: on top rope, leading, or whichever the
   * climber says.
   *
   * Saved on the spot like the tick beside it rather than with the form below,
   * and **editable whatever state the round is in**, unlike the classes and the
   * counts: a class set to lead-only by mistake has to be fixable while the
   * round is on. The server refuses a change that would contradict a card
   * already written, or a route forced the other way — which is what the toast
   * says.
   */
  const setClassStyle = async (categoryId: string, style: LeadClassStyle) => {
    if (!slug || !round) return;

    setBusy(`style-${categoryId}`);
    try {
      const saved = await competitionRoundsService.updateRound(slug, round.id, {
        category_styles: { [categoryId]: style },
      });
      setRound((current) => (current ? { ...current, category_styles: saved.category_styles } : current));
    } catch (error) {
      toast({
        title: t('gymAdmin.rounds.styles.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setBusy(null);
    }
  };

  /** How the given class climbs, as the round says. Lead unless set otherwise. */
  const classStyleOf = (categoryId: string): LeadClassStyle =>
    round?.category_styles?.[categoryId] ?? 'lead';

  /**
   * Whether one route may be forced to a style — which is only a question where
   * the class climbing it has a choice to be taken away. A route the whole round
   * shares is asked of every class that climbs it.
   */
  const canForceStyle = (categoryId: string | null) => {
    if (categoryId) return classStyleOf(categoryId) === 'both';

    return categories.some(
      (category) => linked.has(category.id) && classStyleOf(category.id) === 'both'
    );
  };

  /**
   * The name, the planned start and the length. The length is what the start
   * dialog proposes, so this is where a round is given one — and a blank is a
   * real answer: the round then runs until somebody stops it.
   */
  const saveSchedule = async () => {
    if (!slug || !round) return;

    const minutes = schedule.duration.trim();
    if (minutes !== '' && !(Number(minutes) > 0 && Number.isInteger(Number(minutes)))) {
      toast({ title: t('gymAdmin.rounds.invalidDuration'), variant: 'destructive' });
      return;
    }

    setBusy('schedule');
    try {
      const saved = await competitionRoundsService.updateRound(slug, round.id, {
        name: schedule.name.trim(),
        start_time: datetimeLocalToIso(schedule.startTime),
        duration_minutes: minutes === '' ? null : Number(minutes),
      });
      setRound(saved);
      setSchedule(toSchedule(saved));
      // A renamed round is renamed in the menu too.
      refreshCompetitionRounds();
      toast({ title: t('gymAdmin.saved') });
    } catch (error) {
      toast({
        title: t('gymAdmin.rounds.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setBusy(null);
    }
  };

  /**
   * Turns the typed numbers into the round's problems. The server creates or
   * removes blocks from the end until each class has what was asked, naming the
   * new ones after their ordinal — so this is the whole of "set up the round".
   *
   * Only the classes the round runs are sent: a class it does not run has no
   * blocks to give, and sending zero for it would read as "remove them".
   */
  const saveBlocks = async () => {
    if (!slug || !round) return;

    const blocks = categories
      .filter((category) => linked.has(category.id))
      .map((category) => ({
        category_id: category.id,
        count: parseBlocks(blockDrafts[category.id]),
      }));

    if (blocks.some((block) => block.count === null)) {
      toast({ title: t('gymAdmin.rounds.invalidBlockCount'), variant: 'destructive' });
      return;
    }

    setBusy('blocks');
    try {
      await competitionRoundsService.setBlockCounts(
        slug,
        round.id,
        blocks as { category_id: string; count: number }[]
      );
      if (roundId) await load(slug, roundId);
      toast({ title: t('gymAdmin.saved') });
    } catch (error) {
      toast({
        title: t('gymAdmin.rounds.blocksSaveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setBusy(null);
    }
  };

  /**
   * Writes the holds on each route that changed, one route at a time.
   *
   * Per route rather than in one call because that is what a route's holds are —
   * a measurement of one piece of wall — and because they stay editable after the
   * round has started, unlike its shape: a miscounted route has to be fixable
   * while it is being climbed. The server refuses a count below a card already
   * recorded on the route, which is the one thing a correction can get wrong.
   */
  const saveHolds = async () => {
    if (!slug || !round) return;

    const changes: {
      id: string;
      holds?: number | null;
      forced?: LeadClimbStyle | null;
    }[] = [];

    for (const [id, typed] of Object.entries(holdsDrafts)) {
      const parsed = parseHoldsCount(typed);
      if (parsed === 'invalid') {
        toast({ title: t('gymAdmin.rounds.invalidHoldsCount'), variant: 'destructive' });
        return;
      }

      const route = round.round_climbs?.find((climb) => climb.id === id);
      const change: { id: string; holds?: number | null; forced?: LeadClimbStyle | null } = { id };

      if (parsed !== (route?.holds_count ?? null)) change.holds = parsed;

      // '' is the way a route hands the choice back to its class, so an empty
      // draft is a real answer here and not a half-typed one.
      const forced = (forcedDrafts[id] || null) as LeadClimbStyle | null;
      if (forced !== (route?.required_climb_style ?? null)) change.forced = forced;

      if ('holds' in change || 'forced' in change) changes.push(change);
    }

    if (changes.length === 0) return;

    setBusy('holds');
    try {
      for (const change of changes) {
        await competitionRoundsService.updateRoundClimb(slug, round.id, change.id, {
          ...('holds' in change ? { holds_count: change.holds } : {}),
          ...('forced' in change ? { required_climb_style: change.forced } : {}),
        });
      }
      if (roundId) await load(slug, roundId);
      toast({ title: t('gymAdmin.saved') });
    } catch (error) {
      toast({
        title: t('gymAdmin.rounds.holdsSaveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
      // Whatever went in before the refusal is in the server's copy, so the
      // form is refilled from it rather than left showing what was typed.
      if (roundId) await load(slug, roundId);
    } finally {
      setBusy(null);
    }
  };

  /**
   * Makes the next turn from this one and opens its setup, where whatever
   * differs about it is touched up. Offered whatever state this round is in:
   * only its shape is copied, never what happened on it.
   */
  const duplicateRound = async (data: { name: string; start_time: string | null }) => {
    if (!slug || !round) return;

    try {
      const copy = await competitionRoundsService.duplicateRound(slug, round.id, data);
      // The copy is a new row in the menu.
      refreshCompetitionRounds();
      toast({ title: t('gymAdmin.rounds.duplicate.done') });
      navigate(placePath(`/rounds/${copy.id}/edit`));
    } catch (error) {
      toast({
        title: t('gymAdmin.rounds.duplicate.error'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
      throw error;
    }
  };

  if (competitionLoading || loading) return <Skeleton className="h-96 w-full max-w-6xl" />;
  if (!competition || !slug || !round) return null;

  return (
    <div className="max-w-6xl space-y-6">
      {/* Where the day is actually spent. Same round, the other page. */}
      <HeaderActions>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => navigate(placePath(`/rounds/${round.id}`))}
          data-testid="open-round-scores"
        >
          <ListOrdered className="size-4" aria-hidden />
          {t('gymAdmin.roundScores.title')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => setDuplicating(true)}
          data-testid="duplicate-round"
        >
          <Copy className="size-4" aria-hidden />
          {t('gymAdmin.rounds.duplicate.button')}
        </Button>
      </HeaderActions>

      <DuplicateRoundDialog
        round={round}
        open={duplicating}
        onOpenChange={setDuplicating}
        onDuplicate={duplicateRound}
      />

      <Card>
        <CardHeader className="space-y-1">
          {/* Where it is in its life is said here and changed on the board: these
              are the day's decisions, and they are taken with the scoreboard
              open. */}
          <CardTitle className="flex flex-wrap items-center gap-2">
            {round.name}
            <Badge variant={STATUS_VARIANT[round.status]} data-testid="round-status">
              {t(`gymAdmin.rounds.status.${round.status}`)}
            </Badge>
            {round.running && round.seconds_remaining !== null && (
              <RoundCountdown
                secondsRemaining={round.seconds_remaining}
                onExpire={() => slug && roundId && load(slug, roundId)}
              />
            )}
            {!round.running && (
              <Badge variant="outline" className="gap-1 font-normal">
                <Timer className="size-3" />
                {round.duration_minutes
                  ? t('competitionRounds.plannedDuration', { count: round.duration_minutes })
                  : t('competitionRounds.noDuration')}
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            {round.round_type} •{' '}
            {t('competitionRounds.climbsCount', { count: round.climbs_count })}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {/* What it is called and when it runs. The length is the one the start
              dialog proposes, so this is where a round is given one. */}
          <div className="space-y-3">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="round-name">{t('gymAdmin.rounds.name')}</Label>
                <Input
                  id="round-name"
                  value={schedule.name}
                  onChange={(e) => setSchedule((current) => ({ ...current, name: e.target.value }))}
                  data-testid="round-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="round-start">{t('gymAdmin.rounds.startTime')}</Label>
                <Input
                  id="round-start"
                  type="datetime-local"
                  value={schedule.startTime}
                  onChange={(e) =>
                    setSchedule((current) => ({ ...current, startTime: e.target.value }))
                  }
                  data-testid="round-start"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="round-duration">{t('gymAdmin.rounds.duration')}</Label>
                <Input
                  id="round-duration"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={24 * 60}
                  value={schedule.duration}
                  onChange={(e) =>
                    setSchedule((current) => ({ ...current, duration: e.target.value }))
                  }
                  placeholder={t('gymAdmin.rounds.noLimit')}
                  data-testid="round-duration"
                />
              </div>
            </div>

            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">{t('gymAdmin.rounds.durationHelp')}</p>
              <Button
                size="sm"
                className="shrink-0"
                onClick={saveSchedule}
                disabled={!!busy}
                data-testid="save-round"
              >
                {busy === 'schedule' && <Loader2 className="mr-2 size-4 animate-spin" />}
                {t('gymAdmin.save')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* A turn or a round the whole class climbs: decided with the classes,
          before the round starts, because it decides who is on its board. */}
      <Card>
        <CardContent className="pt-6">
          <label className="flex items-start gap-3 text-sm">
            <Checkbox
              checked={!!round.bookable}
              disabled={!editable || busy === 'bookable'}
              onCheckedChange={toggleBookable}
              className="mt-0.5"
              data-testid="round-bookable"
            />
            <span className="space-y-1">
              <span className="block font-medium">{t('gymAdmin.rounds.bookable.label')}</span>
              <span className="block text-xs text-muted-foreground">
                {editable ? t('gymAdmin.rounds.bookable.help') : t('gymAdmin.rounds.bookable.locked')}
              </span>
              {round.bookable && (
                <span className="block text-xs" data-testid="round-bookings">
                  {round.max_participants
                    ? t('gymAdmin.rounds.bookable.countLimited', {
                        count: round.seats_taken ?? round.bookings_count ?? 0,
                        max: round.max_participants,
                      })
                    : t('gymAdmin.rounds.bookable.count', { count: round.bookings_count ?? 0 })}
                </span>
              )}
            </span>
          </label>

          {/* How many may book it — a turn only: a round the whole class
              climbs has nobody booking it, so no seats to count. */}
          {round.bookable && (
            <div className="mt-4 space-y-2 border-t pt-4">
              <Label htmlFor="round-max-participants">{t('gymAdmin.rounds.bookable.maxParticipants')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="round-max-participants"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={seatsDraft}
                  onChange={(e) => setSeatsDraft(e.target.value)}
                  placeholder={t('gymAdmin.rounds.noLimit')}
                  className="w-32"
                  data-testid="round-max-participants"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={saveSeats}
                  disabled={!!busy || seatsDraft === seatsToDraft(round)}
                  data-testid="save-round-max-participants"
                >
                  {busy === 'seats' && <Loader2 className="mr-2 size-4 animate-spin" />}
                  {t('gymAdmin.save')}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">{t('gymAdmin.rounds.bookable.maxParticipantsHelp')}</p>
              {overbooked && (
                <p
                  className="flex items-start gap-1.5 rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400"
                  role="status"
                  data-testid="round-overbooked"
                >
                  <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
                  {t('gymAdmin.rounds.bookable.overbooked', {
                    count: round.seats_taken ?? 0,
                    max: overbooked,
                  })}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 pt-6">
          <div className="space-y-2">
            <Label>{t('gymAdmin.rounds.categoriesLabel')}</Label>
            <p className="text-xs text-muted-foreground">
              {/* Once the round has run, the help text is the reason the boxes
                  below are dead rather than what to type in them. */}
              {editable
                ? t('gymAdmin.rounds.categoriesHelp')
                : t('gymAdmin.rounds.shapeLocked')}
            </p>
            <div className="space-y-2 pt-1">
              {categories.length === 0 && (
                <span className="text-sm text-muted-foreground">
                  {t('gymAdmin.rounds.noCategories')}
                </span>
              )}
              {categories.map((category) => {
                const runs = linked.has(category.id);

                return (
                  <div
                    key={category.id}
                    className="flex flex-wrap items-center gap-3 rounded-lg border p-3"
                    data-testid="round-category"
                  >
                    <label className="flex flex-1 items-center gap-2 text-sm">
                      <Checkbox
                        checked={runs}
                        disabled={!editable || busy === 'categories'}
                        onCheckedChange={() => toggleCategory(category.id)}
                      />
                      {category.name}
                    </label>

                    {/* How this class climbs a lead round. Beside the count
                        rather than on a page of its own: it is the other thing
                        one decides about a class here, and unlike the count it
                        stays editable once the round has started. */}
                    {runs && isLead && (
                      <Select
                        value={classStyleOf(category.id)}
                        onValueChange={(value) =>
                          setClassStyle(category.id, value as LeadClassStyle)
                        }
                        disabled={busy === `style-${category.id}`}
                      >
                        <SelectTrigger
                          className="w-44"
                          aria-label={t('leadRounds.style.label')}
                          data-testid="class-style"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LEAD_CLIMB_STYLES.map((style) => (
                            <SelectItem key={style} value={style}>
                              {t(leadStyleLabelKey(style))}
                            </SelectItem>
                          ))}
                          <SelectItem value="both">{t('leadRounds.style.both')}</SelectItem>
                        </SelectContent>
                      </Select>
                    )}

                    {/* A class the round does not run has no problems to set,
                        so the box only appears once it is ticked. */}
                    {runs && (
                      <div className="flex items-center gap-2">
                        <Label
                          htmlFor={`blocks-${category.id}`}
                          className="text-xs font-normal text-muted-foreground"
                        >
                          {/* A lead round climbs routes, and the sheet at the
                              wall says so — "Via 1", not "Blocco 1". */}
                          {t(isLead ? 'gymAdmin.rounds.routesLabel' : 'gymAdmin.rounds.blocksLabel')}
                        </Label>
                        <Input
                          id={`blocks-${category.id}`}
                          type="number"
                          inputMode="numeric"
                          min={0}
                          max={50}
                          className="w-20"
                          disabled={!editable}
                          value={blockDrafts[category.id] ?? ''}
                          placeholder="0"
                          onChange={(e) =>
                            setBlockDrafts((current) => ({
                              ...current,
                              [category.id]: e.target.value,
                            }))
                          }
                          data-testid="block-count"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {linked.size > 0 && (
              <div className="flex items-center justify-between gap-3 pt-1">
                {/* The help is about typing a number, so it goes with the boxes
                    being live. Locked, the reason above has already been given. */}
                <p className="text-xs text-muted-foreground">
                  {editable ? t(isLead ? 'gymAdmin.rounds.routesHelp' : 'gymAdmin.rounds.blocksHelp') : ''}
                </p>
                <Button
                  size="sm"
                  className="shrink-0"
                  disabled={!editable || !!busy}
                  onClick={saveBlocks}
                  data-testid="save-blocks"
                >
                  {busy === 'blocks' && <Loader2 className="mr-2 size-4 animate-spin" />}
                  {t(isLead ? 'gymAdmin.rounds.saveRoutes' : 'gymAdmin.rounds.saveBlocks')}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Problems of no class — climbed by every class here — usually left by
          a deleted or unticked category; shown with their cards, removable. */}
      {slug && (
        <UnclassedBlocksCard
          slug={slug}
          roundId={round.id}
          isLead={isLead}
          refreshKey={`${round.category_ids.join(',')}|${round.climbs_count}`}
          onRemoved={() => roundId && load(slug, roundId)}
        />
      )}

      {/* The holds on each route: the one thing a lead round cannot be run
          without, and the reason it refuses to start while a box here is empty.
          Still editable after the round has started, unlike its shape — a
          miscounted route has to be fixable at the wall. */}
      {isLead && routeGroups.length > 0 && (
        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-base">{t('gymAdmin.rounds.holds.title')}</CardTitle>
            <CardDescription>
              {unmeasured > 0
                ? t('gymAdmin.rounds.holds.missing', { count: unmeasured })
                : t('gymAdmin.rounds.holds.allMeasured')}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {routeGroups.map(({ category, routes }) => (
              <div key={category?.id ?? 'shared'} className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {category?.name ?? t('gymAdmin.rounds.holds.sharedRoutes')}
                </p>

                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {routes.map((route) => (
                    <div
                      key={route.id}
                      className="flex items-center justify-between gap-3 rounded-lg border p-3"
                      data-testid="route-holds"
                    >
                      <Label
                        htmlFor={`holds-${route.id}`}
                        className="truncate text-sm font-medium"
                      >
                        {route.display_name ?? route.name}
                      </Label>
                      <div className="flex shrink-0 items-center gap-2">
                        {/* One route taken out of the choice — offered only
                            where there is a choice to take away, which is a
                            class set to "either way". */}
                        {canForceStyle(route.competition_category_id) && (
                          <Select
                            value={forcedDrafts[route.id] || 'free'}
                            onValueChange={(value) =>
                              setForcedDrafts((current) => ({
                                ...current,
                                [route.id]: value === 'free' ? '' : value,
                              }))
                            }
                          >
                            <SelectTrigger
                              className="w-36"
                              aria-label={t('leadRounds.style.routeLabel')}
                              data-testid="route-style"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="free">
                                {t('leadRounds.style.notForced')}
                              </SelectItem>
                              {LEAD_CLIMB_STYLES.map((style) => (
                                <SelectItem key={style} value={style}>
                                  {t('leadRounds.style.forced', {
                                    style: t(leadStyleLabelKey(style)),
                                  })}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}

                        <Input
                          id={`holds-${route.id}`}
                          type="number"
                          inputMode="numeric"
                          min={1}
                          max={200}
                          className="w-20"
                          placeholder="—"
                          value={holdsDrafts[route.id] ?? ''}
                          onChange={(e) =>
                            setHoldsDrafts((current) => ({ ...current, [route.id]: e.target.value }))
                          }
                          data-testid="route-holds-input"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-muted-foreground">{t('gymAdmin.rounds.holds.help')}</p>
              <Button
                size="sm"
                className="shrink-0"
                disabled={!!busy}
                onClick={saveHolds}
                data-testid="save-holds"
              >
                {busy === 'holds' && <Loader2 className="mr-2 size-4 animate-spin" />}
                {t('gymAdmin.save')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
